// Feedback widget: a persistent corner launcher (FAB) plus one founder-voice
// panel. The launcher is always available on every signed-in, non-share route,
// so a user can send feedback the moment they want to. The same panel is ALSO
// offered proactively — after ~60s of genuine foreground product use (a
// backgrounded tab, the welcome screen, or a settings page never add to it) the
// panel opens on its own once per 45-day cycle. Dismissing collapses back to
// the launcher; it never disappears. Submissions go to dashoneProductFeedback
// and land in the product_feedback Firestore collection, whose notify trigger
// pings the team on Slack + email instantly. Self-contained styling on purpose:
// it renders on both the Ant Design analytics pages and the shadcn-scoped
// DashAI pages, so it can't rely on either side's component library or CSS.
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { postWithAuthRetry } from "@dashai/lib/onePSfaClient";
import { trackUserAction } from "@dashai/utils/trackUserAction";
import {
  FEEDBACK_PROMPT_ACTIVE_KEY,
  proactiveOpenEligible,
  type FeedbackPromptState,
} from "./feedbackPromptGate";
import { fetchFeedbackPromptState, stampFeedbackPromptEvent } from "./feedbackPromptStateClient";
import { buildFeedbackContext } from "./buildFeedbackContext";
import { captureScreenshot } from "./captureScreenshot";

const SHOW_AFTER_VISIBLE_MS = 60_000;
const TICK_MS = 5_000;
const MAX_MESSAGE_LEN = 2000;
const SENT_AUTO_HIDE_MS = 4_000;
// The screenshot is captured when the panel opens; on submit we wait at most
// this long for an in-flight capture before sending text-only, so a very fast
// submit never blocks on it.
const SUBMIT_SCREENSHOT_WAIT_MS = 1_500;
// Let the open animation settle before the (CPU-heavy) capture starts.
const CAPTURE_START_DELAY_MS = 350;

// Only genuine product engagement counts toward the proactive threshold: the
// six analytics dashboards (plus their outlet drill-down) and an open chat.
const COUNTED_ROUTE_RE =
  /^\/(market_overview|market_deepdive|execution_view|physical_location|physical_franchise|accounts|key_accounts|outlet\/|ai\/chat\/)/;

const COPY = {
  aria: "A note from the DashONE team",
  launcher: "Send feedback",
  title: "\u{1F44B} A note from the team",
  body:
    "We're Stefan, Dennis, Harry and Zachary, the team building DashONE, and it gets " +
    "better through feedback from the people who use it. Is anything missing, confusing, " +
    "or not working the way you’d expect? Tell us. We read every message ourselves.",
  placeholder: "A metric you’re missing, a confusing view, an idea...",
  send: "Send to the team",
  later: "Maybe later",
  sentTitle: "Thank you \u{1F64F}",
  sentBody: "Your note went straight to the whole team. It genuinely shapes what we build next.",
  error: "That did not go through. Please try again in a moment.",
};

// The launcher and panel sit below Ant Design modals/drawers (z-index 1000) and
// shadcn dialogs so they never cover a dialog the user is working in, but above
// regular page content.
const Z = 900;

const Launcher = styled.button`
  position: fixed;
  right: 18px;
  bottom: 18px;
  width: 52px;
  height: 52px;
  border: none;
  border-radius: 50%;
  background: #0e49b5;
  color: #ffffff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(10, 20, 64, 0.24);
  z-index: ${Z};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #0d3d96;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid #0e49b5;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Card = styled.aside<{ $in: boolean }>`
  position: fixed;
  right: 18px;
  bottom: 18px;
  width: 340px;
  max-width: calc(100vw - 24px);
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(10, 20, 64, 0.16);
  padding: 16px;
  z-index: ${Z};
  font-family: "Inter", sans-serif;
  opacity: ${(p) => (p.$in ? 1 : 0)};
  transform: translateY(${(p) => (p.$in ? "0" : "12px")});
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 14px;
  line-height: 1;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Title = styled.p`
  margin: 0 14px 6px 0;
  font-size: 14.5px;
  font-weight: 700;
  color: #0a1440;
`;

const Body = styled.p`
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 1.55;
  color: #4b5563;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MessageInput = styled.textarea`
  width: 100%;
  min-height: 72px;
  resize: vertical;
  padding: 8px 10px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.45;
  color: #111827;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus-visible {
    outline: 2px solid #0e49b5;
    outline-offset: 1px;
  }
`;

const SendButton = styled.button`
  margin-top: 6px;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  background: #0e49b5;
  color: #ffffff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #0d3d96;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const LaterButton = styled.button`
  padding: 4px;
  border: none;
  background: transparent;
  color: #6b7280;
  font-family: inherit;
  font-size: 12.5px;
  cursor: pointer;

  &:hover {
    color: #374151;
    text-decoration: underline;
  }
`;

const ErrorLine = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: #b42318;
`;

function functionsUrl(): string | null {
  const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
  if (!base) return null;
  return `${base.replace(/\/+$/, "")}/dashoneProductFeedback`;
}

/** True while a modal-style dialog is open, so the proactive open waits its turn. */
function dialogIsOpen(): boolean {
  return Boolean(
    document.querySelector('.ant-modal-mask, .ant-drawer-open, [role="dialog"][data-state="open"]')
  );
}

type Phase = "collapsed" | "open" | "sent";

export default function FeedbackWidget() {
  // The backend takes the sender's identity from the verified Firebase token,
  // so only the workspace label and market travel in the request body.
  const { isAuthenticated, id_client } = useSelector(
    (state: { auth: { isAuthenticated: boolean; id_client: string | null } }) => state.auth
  );
  const market = useSelector(
    (state: { filters?: { currentCountry: string | null } }) => state.filters?.currentCountry ?? null
  );
  const location = useLocation();
  const [phase, setPhase] = useState<Phase>("collapsed");
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);

  // Mirror phase in a ref so the background send path can tell whether the user
  // has since closed the optimistic thank-you — a late failure must not
  // resurrect a panel they already dismissed.
  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // The slide-in transition needs one painted frame at the resting style before
  // $in flips, or the card just pops in fully opaque.
  const panelOpen = phase !== "collapsed";
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!panelOpen) {
      setEntered(false);
      return undefined;
    }
    const id = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(id);
  }, [panelOpen]);

  // Capture the screenshot when the panel OPENS (not on submit), so the ~2s
  // html2canvas pass overlaps the time the user spends reading the prompt and
  // typing their message — keeping the submit itself instant. Held as a promise
  // the submit path consumes. The page behind the panel is static once open,
  // and the capture excludes the widget's own subtree.
  const screenshotRef = useRef<Promise<string | null> | null>(null);
  useEffect(() => {
    if (!panelOpen) {
      screenshotRef.current = null;
      return undefined;
    }
    const id = window.setTimeout(() => {
      screenshotRef.current = captureScreenshot();
    }, CAPTURE_START_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [panelOpen]);

  // Public share views render without a signed-in session of their own, but a
  // signed-in teammate opening a share link shouldn't get a widget mid-present.
  const isShareRoute = /^\/ai\/(insights|chat)\/s\//.test(location.pathname);
  // Kept current via an effect rather than mutated during render; the timer tick
  // and submit read it for route-gating and the reported page.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Whether the panel currently open was opened proactively (vs. by the user
  // clicking the launcher). Only a proactive open stamps a dismissal against the
  // server-side cadence — a manual open+close never penalises the user.
  const openedProactivelyRef = useRef(false);
  // At most one proactive auto-open per session; after that the launcher is the
  // only path (the server-side cadence gates the offer across sessions).
  const proactiveOfferedRef = useRef(false);

  // Per-user cadence state, fetched once on mount from the server. Held in a ref
  // (the timer reads it) with a loaded flag to (re-)arm the timer. Stays null if
  // the fetch fails, so proactiveOpenEligible() returns false — the proactive
  // open fails toward not-nagging; the launcher still works on demand.
  const promptStateRef = useRef<FeedbackPromptState | null>(null);
  const [promptStateLoaded, setPromptStateLoaded] = useState(false);
  useEffect(() => {
    if (!isAuthenticated || isShareRoute) return undefined;
    let cancelled = false;
    void fetchFeedbackPromptState().then((state) => {
      if (cancelled) return;
      promptStateRef.current = state;
      setPromptStateLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isShareRoute]);

  // Move focus into the panel when the user opened it themselves, so keyboard /
  // screen-reader users land on the textarea instead of losing focus to <body>
  // when the launcher unmounts. A PROACTIVE auto-open must NOT steal focus (it
  // would interrupt whatever the user is doing), so this is gated on a manual open.
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (phase === "open" && !openedProactivelyRef.current) {
      textareaRef.current?.focus();
    }
  }, [phase]);

  // Clear the sent-state auto-hide timer on unmount so it can't fire setState
  // after the component is gone.
  const autoHideRef = useRef<number | undefined>(undefined);
  useEffect(
    () => () => {
      if (autoHideRef.current !== undefined) window.clearTimeout(autoHideRef.current);
    },
    []
  );

  // Cross-tab coordination: at most one PROACTIVE open across a user's open
  // tabs. Auto-opening writes localStorage; the storage event fires in the
  // OTHER tabs only, standing their pending auto-opens down. The launcher stays
  // in every tab — only the unprompted offer is de-duped.
  const suppressedRef = useRef(false);
  const messageRef = useRef("");
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== FEEDBACK_PROMPT_ACTIVE_KEY) return;
      suppressedRef.current = true;
      // Retract only an untouched, proactively-opened panel — never one the
      // user opened themselves or is already typing in.
      setPhase((p) =>
        p === "open" && openedProactivelyRef.current && !messageRef.current.trim()
          ? "collapsed"
          : p
      );
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Proactive auto-open timer: only while collapsed, eligible per the server-side
  // cadence (loaded on mount), not suppressed by another tab, and not already
  // offered this session.
  useEffect(() => {
    if (
      !isAuthenticated ||
      isShareRoute ||
      phase !== "collapsed" ||
      suppressedRef.current ||
      proactiveOfferedRef.current ||
      !promptStateLoaded
    )
      return undefined;
    if (!proactiveOpenEligible(promptStateRef.current, Date.now())) return undefined;

    let visibleMs = 0;
    let last = performance.now();
    const tick = window.setInterval(() => {
      const now = performance.now();
      if (!document.hidden && COUNTED_ROUTE_RE.test(pathnameRef.current)) {
        visibleMs += now - last;
      }
      last = now;
      if (visibleMs < SHOW_AFTER_VISIBLE_MS) return;
      if (suppressedRef.current) {
        window.clearInterval(tick); // another tab is already asking
        return;
      }
      if (dialogIsOpen()) return; // try again next tick
      window.clearInterval(tick);
      try {
        window.localStorage.setItem(FEEDBACK_PROMPT_ACTIVE_KEY, String(Date.now()));
      } catch {
        // Best-effort cross-tab claim; a private-mode failure just means the
        // other tabs aren't stood down.
      }
      openedProactivelyRef.current = true;
      proactiveOfferedRef.current = true;
      // Record the offer server-side so the cadence advances across devices.
      void stampFeedbackPromptEvent("offered");
      setPhase("open");
    }, TICK_MS);
    return () => window.clearInterval(tick);
  }, [isAuthenticated, isShareRoute, phase, promptStateLoaded]);

  if (!isAuthenticated || isShareRoute) return null;

  if (phase === "collapsed") {
    return (
      <Launcher
        data-feedback-widget=""
        type="button"
        aria-label={COPY.launcher}
        title={COPY.launcher}
        onClick={() => {
          openedProactivelyRef.current = false;
          // A manual open consumes this session's proactive offer, so dismissing
          // it never triggers a surprise auto-open ~60s later.
          proactiveOfferedRef.current = true;
          setPhase("open");
        }}
      >
        {"\u{1F4AC}"}
      </Launcher>
    );
  }

  const collapse = () => {
    setPhase("collapsed");
    setMessage("");
    messageRef.current = "";
    setError(false);
  };

  const dismiss = () => {
    // Only a proactive offer stamps a dismissal (starting the server-side
    // cool-down); a manual open records nothing, so the user can reopen freely.
    if (openedProactivelyRef.current) void stampFeedbackPromptEvent("dismissed");
    collapse();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || phase !== "open") return;
    const url = functionsUrl();
    if (!url) return;
    // Optimistic: acknowledge instantly and do the network work (screenshot
    // wait + upload, which the backend runs synchronously before responding) in
    // the background, so the panel never sits on a spinner. Best-effort — a
    // failure reopens the form with the message intact for a retry; a success
    // is never lost.
    setError(false);
    setPhase("sent");
    void sendFeedback(url, trimmed);
  };

  const sendFeedback = async (url: string, trimmed: string) => {
    try {
      // Consume the screenshot captured when the panel opened. If it isn't ready
      // yet (a very fast submit), wait only briefly, then send text-only rather
      // than block the send. Best-effort — never throws, never a lost message.
      const pending = screenshotRef.current ?? Promise.resolve(null);
      const screenshot = await Promise.race([
        pending.catch(() => null),
        new Promise<null>((resolve) => {
          window.setTimeout(() => resolve(null), SUBMIT_SCREENSHOT_WAIT_MS);
        }),
      ]);
      const res = await postWithAuthRetry(url, {
        message: trimmed.slice(0, MAX_MESSAGE_LEN),
        context: buildFeedbackContext({
          page: pathnameRef.current,
          id_client,
          market,
        }),
        ...(screenshot ? { screenshot } : {}),
      });
      if (!res.ok) throw new Error(String(res.status));
      // Usage tracking via the Firestore login_events path. Fire-and-forget —
      // it never throws or blocks the UI. The funnel/abandonment events
      // (offer-shown, opened-then-closed) are deliberately not tracked yet.
      void trackUserAction({
        eventType: "feedback_submitted",
        idClient: id_client ?? undefined,
        metadata: { page: pathnameRef.current, market },
      });
      // A submission suppresses the proactive offer for the next cycle too —
      // stamped server-side by the feedback sink (dashoneProductFeedback), so no
      // client cadence write is needed here. Start the thank-you auto-collapse
      // only once the send has actually landed — and only if the user is still
      // looking at the thank-you (they may have closed it already).
      if (phaseRef.current === "sent") {
        autoHideRef.current = window.setTimeout(collapse, SENT_AUTO_HIDE_MS);
      }
    } catch {
      // Failed after the optimistic ack: reopen the form so the user can retry,
      // but only if the thank-you is still showing — never resurrect a panel the
      // user already closed. `message` is still in state (only collapse() clears
      // it, and it hasn't run).
      if (phaseRef.current === "sent") {
        setError(true);
        setPhase("open");
      }
    }
  };

  const sent = phase === "sent";

  return (
    <Card data-feedback-widget="" $in={entered} role="complementary" aria-label={COPY.aria}>
      <CloseButton type="button" aria-label="Close" onClick={sent ? collapse : dismiss}>
        {"✕"}
      </CloseButton>
      <Title>{sent ? COPY.sentTitle : COPY.title}</Title>
      {/* aria-live so the flip to the thank-you (or back) is announced to
          screen readers; the founder copy on first open is the initial content
          of the region, so it isn't announced as a change. */}
      <Body aria-live="polite">{sent ? COPY.sentBody : COPY.body}</Body>
      {!sent && (
        <Form onSubmit={submit}>
          <MessageInput
            ref={textareaRef}
            value={message}
            onChange={(e) => {
              messageRef.current = e.target.value;
              setMessage(e.target.value);
            }}
            maxLength={MAX_MESSAGE_LEN}
            required
            placeholder={COPY.placeholder}
            aria-label="Your feedback"
          />
          {error && <ErrorLine role="alert">{COPY.error}</ErrorLine>}
          <SendButton type="submit" disabled={!message.trim()}>
            {COPY.send}
          </SendButton>
          <LaterButton type="button" onClick={dismiss}>
            {COPY.later}
          </LaterButton>
        </Form>
      )}
    </Card>
  );
}
